import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, MapPin, Search, Store } from 'lucide-react';
import { fetchPortalContext, fetchPortalStores } from '@/lib/portalApi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PortalShell } from './PortalShell';
import {
  PortalErrorBlock,
  PortalLoadingBlock,
  portalLabelClass,
  portalPageSubtitleClass,
  portalPageTitleClass,
} from './portalUi';

function queryErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function PaginationBar({
  page,
  pageSize,
  totalCount,
  onPageChange,
}: {
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
}) {
  if (totalCount <= pageSize) return null;

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalCount);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3 text-sm text-muted-foreground">
      <span>Showing {start}–{end} of {totalCount}</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="rounded border border-border px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </button>
        <span>Page {page} of {totalPages}</span>
        <button
          type="button"
          className="rounded border border-border px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function SummaryStat({
  label,
  value,
  detail,
  icon,
}: {
  label: string;
  value: string | number;
  detail: string;
  icon: ReactNode;
}) {
  return (
    <Card>
      <CardContent className="flex items-start justify-between gap-3 pt-6">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">{value}</p>
          <p className="mt-1 text-xs text-muted-foreground">{detail}</p>
        </div>
        <span className="rounded-lg bg-primary/10 p-2 text-primary">{icon}</span>
      </CardContent>
    </Card>
  );
}

export function PortalStoresPage() {
  const { data: context, isLoading: contextLoading, isError: contextError, error: contextErrorDetail } = useQuery({
    queryKey: ['portal', 'context'],
    queryFn: fetchPortalContext,
  });

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [retailerId, setRetailerId] = useState<number | ''>('');
  const pageSize = 25;

  useEffect(() => {
    setPage(1);
  }, [search, retailerId]);

  const {
    data: stores,
    isLoading: storesLoading,
    isError: storesError,
    error: storesErrorDetail,
  } = useQuery({
    queryKey: ['portal', 'stores', search, page, pageSize, retailerId],
    queryFn: () => fetchPortalStores({
      search,
      page,
      pageSize,
      retailerId: retailerId === '' ? undefined : retailerId,
    }),
    enabled: !!context?.features.stores,
  });

  const regionCount = useMemo(() => {
    const regions = new Set(
      (stores?.items ?? [])
        .map((store) => store.region?.trim())
        .filter((region): region is string => Boolean(region)),
    );
    return regions.size;
  }, [stores?.items]);

  if (contextLoading) return <PortalLoadingBlock />;
  if (contextError || !context) {
    return <PortalErrorBlock message={queryErrorMessage(contextErrorDetail, 'Could not load portal context.')} />;
  }

  if (!context.features.stores) {
    return (
      <PortalShell context={context}>
        <PortalErrorBlock message="Store directory is not enabled for your account." />
      </PortalShell>
    );
  }

  const showRetailerFilter = context.allowedRetailers.length > 1;
  const retailerLabel = context.allowedRetailers.map((retailer) => retailer.name).join(', ');

  return (
    <PortalShell context={context}>
      <div className="space-y-6">
        <div>
          <h2 className={portalPageTitleClass}>Stores</h2>
          <p className={portalPageSubtitleClass}>
            Read-only store directory for {retailerLabel}.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <SummaryStat
            label="Stores"
            value={stores?.totalCount ?? '—'}
            detail={search || retailerId !== '' ? 'Matching current filters' : 'Across your retailer grants'}
            icon={<Store className="h-5 w-5" />}
          />
          <SummaryStat
            label="Retailers"
            value={context.allowedRetailers.length}
            detail="Visible in this portal account"
            icon={<Building2 className="h-5 w-5" />}
          />
          <SummaryStat
            label="Regions"
            value={storesLoading ? '—' : regionCount}
            detail="On this results page"
            icon={<MapPin className="h-5 w-5" />}
          />
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Store directory</CardTitle>
            <CardDescription>Search by name, number, region, or address.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3 pb-0">
            {showRetailerFilter ? (
              <label className="flex flex-col gap-1 text-sm">
                <span className={portalLabelClass}>Retailer</span>
                <select
                  className="min-w-[180px] rounded border border-border bg-background px-3 py-2"
                  value={retailerId}
                  onChange={(event) => {
                    const value = event.target.value;
                    setRetailerId(value === '' ? '' : Number(value));
                  }}
                >
                  <option value="">All retailers</option>
                  {context.allowedRetailers.map((retailer) => (
                    <option key={retailer.id} value={retailer.id}>{retailer.name}</option>
                  ))}
                </select>
              </label>
            ) : null}
            <label className="relative min-w-[240px] flex-1 text-sm">
              <span className={portalLabelClass}>Search</span>
              <Search className="pointer-events-none absolute left-3 top-[2.15rem] h-4 w-4 text-muted-foreground" />
              <Input
                className="mt-1 pl-9"
                placeholder="Store name, number, region, or address"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
          </CardContent>

          {storesLoading ? (
            <CardContent>
              <PortalLoadingBlock />
            </CardContent>
          ) : storesError ? (
            <CardContent>
              <PortalErrorBlock message={queryErrorMessage(storesErrorDetail, 'Could not load stores.')} />
            </CardContent>
          ) : (
            <>
              <div className="overflow-x-auto px-1 pb-1">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Store</TableHead>
                      <TableHead>Number</TableHead>
                      {showRetailerFilter ? <TableHead>Retailer</TableHead> : null}
                      <TableHead>Region</TableHead>
                      <TableHead>Format</TableHead>
                      <TableHead>Address</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(stores?.items ?? []).map((store) => (
                      <TableRow key={store.storeId}>
                        <TableCell className="font-medium">{store.storeName}</TableCell>
                        <TableCell className="text-muted-foreground">{store.storeNumber ?? '—'}</TableCell>
                        {showRetailerFilter ? <TableCell>{store.retailerName}</TableCell> : null}
                        <TableCell>{store.region ?? '—'}</TableCell>
                        <TableCell>{store.formatName ?? '—'}</TableCell>
                        <TableCell className="max-w-xs truncate">{store.address}</TableCell>
                      </TableRow>
                    ))}
                    {(stores?.items ?? []).length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={showRetailerFilter ? 6 : 5}
                          className="py-10 text-center text-muted-foreground"
                        >
                          No stores match your filters.
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </div>
              {stores ? (
                <PaginationBar
                  page={stores.page}
                  pageSize={stores.pageSize}
                  totalCount={stores.totalCount}
                  onPageChange={setPage}
                />
              ) : null}
            </>
          )}
        </Card>
      </div>
    </PortalShell>
  );
}
